import HttpUtil from '../../util/HttpUtil';
import SafeUtil from '../../util/SafeUtil';
import qs from 'qs';
import React from 'react';
import ViewBase from './ViewBase';
import Sun from '../global/Sun';
import { WebResultCode } from './WebResultCode';
import MessageBoxUtil from '../../util/MessageBoxUtil';
import Lang from '../global/Lang';
import Moon from '../global/Moon';

/**
 * 基类。带有网络请求能力的基类
 * 继承了该类就表示具有了去服务器请求的能力。
 */
export default class HttpBase extends ViewBase {
  static lastLoginErrorTimestamp = 0;

  //是否需要自动刷新State
  needReactComponentUpdate: boolean = true;

  //当前是否正在进行http请求
  loading: boolean = false;

  //请求http的时候是否有错误
  errorMessage: string | null = null;

  //我们认为每个实体都会存放于某个react组件中，当然可以不传入。
  constructor(reactComponent?: React.Component | null) {
    super();

    if (reactComponent) {
      this.reactComponent = reactComponent;
    }
  }

  //更新当前的视图，只在需要更新的情况下才更新。
  updateUI() {
    if (this.needReactComponentUpdate && this.reactComponent) {
      ViewBase.updateComponentUI(this.reactComponent, this);
    }
  }

  /**
   * 转跳到登录页面
   */
  jumpLogin() {
    console.log('=== jumpLogin() 方法开始执行 ===');
    console.log('清除本地存储并跳转到登录页面');
    
    // 立即清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    
    // 立即跳转到登录页面
    Sun.navigateTo('/user/login');
    console.log('=== jumpLogin() 方法执行完成 ===');
  }

  /**
   * 转跳到网站安装页面
   */
  jumpInstall() {
    Sun.navigateTo('/install/index');
  }

  // 错误码到中文消息的映射
  private static errorMessageMap: { [key: string]: string } = {
    'OK': 'operation.success',
    'BAD_REQUEST': 'error.badRequest',
    'NEED_SHARE_CODE': 'error.needShareCode',
    'SHARE_CODE_ERROR': 'error.shareCodeError',
    'LOGIN': 'error.notLogin',
    'USER_DISABLED': 'error.userDisabled',
    'UNAUTHORIZED': 'error.unauthorized',
    'NOT_FOUND': 'error.notFound',
    'METHOD_NOT_ALLOWED': 'error.methodNotAllowed',
    'CONFLICT': 'error.conflict',
    'PRECONDITION_FAILED': 'error.preconditionFailed',
    'UNSUPPORTED_MEDIA_TYPE': 'error.unsupportedMediaType',
    'RANGE_NOT_SATISFIABLE': 'error.rangeNotSatisfiable',
    'NOT_INSTALLED': 'error.notInstalled',
    'SERVER': 'error.serverError',
    'UNKNOWN': 'error.unknownError'
  };

  // 根据错误码获取本地化消息（支持大小写不敏感）
  private getTranslatedMessage(errorCode: string): string {
    // 统一转换为大写进行查找
    const normalizedCode = errorCode.toUpperCase();
    const messageKey = HttpBase.errorMessageMap[normalizedCode] || 'error.unknownError';
    const translatedMessage = Lang.t(messageKey);
    
    // 如果翻译成功且不是原始错误码，返回翻译后的消息
    if (translatedMessage && translatedMessage !== messageKey) {
      return translatedMessage;
    }
    
    // 否则返回原始错误码
    return errorCode;
  }

  //从一个返回中获取出其错误信息。适配各种错误的类型。
  getErrorMessage(response: any): string {
    if (response === null || response === undefined) {
      return Lang.t('error.unknownError') || '未知错误';
    }

    // 首先检查是否是后端返回的标准错误结构
    if (response.data && typeof response.data === 'object') {
      const data = response.data;
      
      // 如果有错误码，使用翻译（支持大小写不敏感）
      if (data.code && typeof data.code === 'string') {
        const translatedMessage = this.getTranslatedMessage(data.code);
        
        // 如果有详细消息，结合使用
        if (data.msg && data.msg !== data.code) {
          return `${translatedMessage}: ${data.msg}`;
        }
        if (data.message && data.message !== data.code) {
          return `${translatedMessage}: ${data.message}`;
        }
        
        return translatedMessage;
      }
      
      // 处理其他标准错误结构
      if (data.msg) return data.msg;
      if (data.message) return data.message;
    }

    if (typeof response === 'string') {
      return response;
    }

    if (response instanceof Error) {
      return response.message || response.toString();
    }

    // 处理 axios 错误响应
    if (response.isAxiosError) {
      if (response.response && response.response.data) {
        const data = response.response.data;
        
        // 处理后端错误码（支持大小写不敏感）
        if (data.code && typeof data.code === 'string') {
          return this.getTranslatedMessage(data.code);
        }
        
        if (typeof data === 'string') return data;
        if (data.message) return data.message;
        if (data.msg) return data.msg;
      }
      return response.message || (Lang.t('error.unknownError') || '未知错误');
    }

    // 处理 HTTP 响应
    if (response.data) {
      const data = response.data;
      
      if (data.code && typeof data.code === 'string') {
        return this.getTranslatedMessage(data.code);
      }
      
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.msg) return data.msg;
      if (data.error && data.error.message) return data.error.message;
    }

    if (response.message) return response.message;
    if (response.msg) return response.msg;

    // 处理状态码
    if (response.status) {
      switch (response.status) {
        case 400: 
          return this.getTranslatedMessage('BAD_REQUEST');
        case 401: 
          return this.getTranslatedMessage('UNAUTHORIZED');
        case 403: 
          return this.getTranslatedMessage('USER_DISABLED');
        case 404: 
          return this.getTranslatedMessage('NOT_FOUND');
        case 413: 
          return Lang.t('matter.sizeExceedLimitGeneric') || '文件大小超过限制';
        case 500: 
          return this.getTranslatedMessage('SERVER');
        default: 
          return Lang.t('error.serverError') || `服务器错误 (${response.status})`;
      }
    }

    return Lang.t('error.unknownError') || '未知错误';
  }

  // 判断是否是网络错误
  isNetworkError(error: any): boolean {
    if (!error) return false;
    
    if (error instanceof Error) {
      const errorMessage = error.message || error.toString();
      return errorMessage.includes('ERR_ACCESS_DENIED') ||
             errorMessage.includes('Network Error') ||
             errorMessage.includes('Failed to fetch') ||
             errorMessage.includes('net::') ||
             errorMessage.includes('Network request failed') ||
             errorMessage.includes('ECONNREFUSED') ||
             errorMessage.includes('ETIMEDOUT');
    }
    
    if (error.isAxiosError) {
      const isNetworkError = !error.response || 
             error.response.status === 0 ||
             error.code === 'ECONNABORTED' || 
             error.code === 'ERR_NETWORK' ||
             error.code === 'NETWORK_ERROR';
      
      return isNetworkError;
    }
    
    if (error.response) {
      const status = error.response.status;
      const isNetworkError = status === 0 || 
             (status >= 500 && status < 600) ||
             status === 408 ||
             status === 504;
      
      return isNetworkError;
    }
    
    return !error.response && error.message && error.message.includes('Network Error');
  }

  // 默认错误处理器
  defaultErrorHandler(err: any, failureCallback?: (msg: string, err: any) => void) {
    console.log('=== defaultErrorHandler 开始执行 ===');
    console.log('错误对象:', err);
    
    // 获取翻译后的错误消息
    const errorMsg = this.getErrorMessage(err);
    
    console.log('翻译后的错误消息:', errorMsg);
    
    // 显示错误消息
    MessageBoxUtil.error(errorMsg);
    
    // 精确判断是否需要跳转登录
    if (this.shouldJumpToLogin(err)) {
      console.log('需要跳转登录，执行跳转逻辑');
      this.handleLoginRedirect();
    } else {
      console.log('不需要跳转登录，仅显示错误提示');
    }
    
    if (typeof failureCallback === 'function') {
      failureCallback(errorMsg, err);
    }
    
    console.log('=== defaultErrorHandler 执行完成 ===');
  }



  // 处理登录跳转 - 移除防重复逻辑
  private handleLoginRedirect(): void {
    console.log('=== handleLoginRedirect 开始执行 ===');
    
    // 立即清除本地存储
    console.log('清除本地存储');
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    
    // 立即跳转，不延迟
    console.log('立即跳转到登录页面');
    this.jumpLogin();
    
    console.log('=== handleLoginRedirect 执行完成 ===');
  }

  async defaultErrorHandlerWithRetry(response: any, errorCallback?: any, retryCallback?: Function, maxRetries: number = 3, currentRetry: number = 0) {
    try {
      this.defaultErrorHandler(response, errorCallback);
      
      if (this.isNetworkError(response) && retryCallback && currentRetry < maxRetries) {
        const retryCount = currentRetry + 1;
        const remainingRetries = maxRetries - currentRetry;
        
        const shouldRetry = window.confirm(
          `网络连接失败（${retryCount}/${maxRetries}），是否重试？`
        );
        
        if (shouldRetry) {
          const delay = Math.pow(2, currentRetry) * 1000;
          console.log(`将在 ${delay}ms 后重试...`);
          
          setTimeout(() => {
            retryCallback(() => {
              this.defaultErrorHandlerWithRetry(response, errorCallback, retryCallback, maxRetries, retryCount);
            });
          }, delay);
        }
      }
    } catch (error) {
      if (typeof errorCallback === 'function') {
        errorCallback('处理错误时发生异常', response);
      }
    }
  }

  specialErrorHandler(response: any) {
    console.log('=== specialErrorHandler 开始执行 ===');
    console.log('response:', response);
    console.log('response.data:', response?.data);
    
    if (!response || !response.data) {
      console.log('response 或 response.data 为空，返回 false');
      return false;
    }
  
    const responseCode = response.data['code'];
    const normalizedCode = responseCode ? responseCode.toUpperCase() : '';
    
    //console.log('检测到的错误码:', responseCode);
    //console.log('标准化后的错误码:', normalizedCode);
    
    // 直接使用 window.location.pathname 判断分享页面
    const isSharePage = window.location.pathname.startsWith('/share/detail');
    //console.log('是否为分享页面:', isSharePage);
    
    // 分享页面处理
    if (isSharePage) {
      if (normalizedCode === 'OK') {
        //console.log('分享页面成功响应，不拦截');
        return false; // 让 successCallback 正常执行
      } else {
        console.log('分享页面遇到错误，忽略但不跳转');
        // 可选：显示错误提示（为了不影响分享体验，这里不提示）
        // const errorMessage = this.getTranslatedMessage(normalizedCode) || '未知错误';
        // MessageBoxUtil.error(errorMessage);
        return true; // 阻止默认错误处理（不跳转登录，不显示错误）
      }
    }
    
    // 非分享页面的 LOGIN 错误：跳转登录
    if (normalizedCode === 'LOGIN') {
      console.log('检测到 LOGIN 错误，开始处理');
      const errorMessage = this.getTranslatedMessage('LOGIN');
      MessageBoxUtil.error(errorMessage);
      this.handleLoginRedirect();
      return true;
    }
    
    // 其他错误（如 UNAUTHORIZED）只显示提示，不跳转
    if (normalizedCode === 'UNAUTHORIZED') {
      console.log('检测到 UNAUTHORIZED 错误，仅显示提示');
      const errorMessage = this.getTranslatedMessage('UNAUTHORIZED');
      MessageBoxUtil.error(errorMessage);
      return true;
    }
  
    console.log('不是特殊错误，返回 false');
    return false;
  }
  private shouldJumpToLogin(error: any): boolean {
    if (!error) return false;
    
    const isSharePage = window.__IS_SHARE_PAGE__ === true;
    if (isSharePage) {
      return false; // 分享页面不跳转
    }
    
    // 检查错误码
    let errorCode: string | undefined;
    
    if (error.data && error.data.code) {
      errorCode = error.data.code.toUpperCase();
    } else if (error.response && error.response.data && error.response.data.code) {
      errorCode = error.response.data.code.toUpperCase();
    }
    
    // 只有 LOGIN 错误才可能跳转
    if (errorCode === 'LOGIN') {
      return true;
    }
    
    return false;
  }
  /**
   * 基类中的http请求会去统一处理错误情况。
   * 1.登录过期
   *
   */
  httpGet(
    url: any,
    params = {},
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any,
    opts?: any
  ) {

    if (window.location.pathname.startsWith('/share/detail') && url === '/api/user/info') {
      console.log('分享页面拦截用户信息请求，直接返回成功');
      const fakeResponse = { data: { code: 'OK', data: {} } };
      SafeUtil.safeCallback(successCallback)(fakeResponse);
      SafeUtil.safeCallback(finallyCallback)();
      return Promise.resolve(fakeResponse);
    }
    let that = this;

    if (!opts) {
      opts = {};
    }

    if (!opts['headers']) {
      opts['headers'] = {};
    }
    
    const currentUser = Moon.getSingleton().user;
    if (currentUser && currentUser.hasLogin() && currentUser.uuid) {
      opts['headers']['X-User-Uuid'] = currentUser.uuid;
    }

    that.loading = true;
    that.updateUI();

    console.log('=== httpGet 开始执行 ===');
    console.log('请求URL:', url);
    console.log('请求参数:', params);

    return HttpUtil.httpGet(
      url,
      params,
      function (response: any) {
        console.log('=== httpGet 成功回调 ===');
        console.log('响应数据:', response);
        
        //有可能正常接口回来的数据也是错误的。交给错误处理器处理。
        if (that.specialErrorHandler(response)) {
          console.log('specialErrorHandler 返回 true，调用 errorCallback');
          SafeUtil.safeCallback(errorCallback)(response);
          return;
        }

        console.log('specialErrorHandler 返回 false，调用 successCallback');
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (err: any) {
        let response = err.response;
        console.log('=== httpGet 错误回调 ===');
        console.log('错误响应:', response);

        //特殊错误情况的通用处理方式
        if (that.specialErrorHandler(response)) {
          console.log('specialErrorHandler 返回 true，跳过默认错误处理');
          return;
        }

        console.log('specialErrorHandler 返回 false，调用默认错误处理');
        that.defaultErrorHandler(response, errorCallback);
      },
      function (res: any) {
        that.loading = false;
        that.updateUI();
        SafeUtil.safeCallback(finallyCallback)(res);
      },
      opts
    );
  }

  /**
   * 基类中纯粹的http get请求。
   *
   */
  httpPureGet(
    url: any,
    params = {},
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any,
    opts?: any
  ) {
    let that = this;

    if (!opts) {
      opts = {};
    }

    if (!opts['headers']) {
      opts['headers'] = {};
    }
    
    const currentUser = Moon.getSingleton().user;
    if (currentUser && currentUser.hasLogin() && currentUser.uuid) {
      opts['headers']['X-User-Uuid'] = currentUser.uuid;
    }

    return HttpUtil.httpGet(
      url,
      params,
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (err: any) {
        SafeUtil.safeCallback(errorCallback)(
          that.getErrorMessage(err.response)
        );
      },
      function (res: any) {
        SafeUtil.safeCallback(finallyCallback)(res);
      },
      opts
    );
  }

  /**
   * 基类中的http请求会去统一处理错误情况。
   * 1.登录过期
   *
   */
  httpPost(
    url: any,
    params = {},
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any,
    opts?: any
  ) {
    // 拦截分享页面的用户信息请求
  // 拦截分享页面的用户信息请求
  if (window.location.pathname.startsWith('/share/detail') && url === '/api/user/info') {
    console.log('分享页面拦截用户信息请求，直接返回成功');
    const fakeResponse = { data: { code: 'OK', data: {} } };
    SafeUtil.safeCallback(successCallback)(fakeResponse);
    SafeUtil.safeCallback(finallyCallback)();
    return Promise.resolve(fakeResponse);
  }
    let that = this;

    if (!opts) {
      opts = {};
    }

    if (!opts['headers']) {
      opts['headers'] = {};
    }
    
    const currentUser = Moon.getSingleton().user;
    if (currentUser && currentUser.hasLogin() && currentUser.uuid) {
      opts['headers']['X-User-Uuid'] = currentUser.uuid;
    }

    that.loading = true;
    that.updateUI();

    let formData = qs.stringify(params);

    if (!opts['headers']) {
      opts['headers'] = {};
    }
    opts['headers']['Content-Type'] = 'application/x-www-form-urlencoded';

    console.log('=== httpPost 开始执行 ===');
    console.log('请求URL:', url);
    console.log('请求参数:', params);

    return HttpUtil.httpPost(
      url,
      formData,
      function (response: any) {
        console.log('=== httpPost 成功回调 ===');
        console.log('响应数据:', response);
        
        //有可能正常接口回来的数据也是错误的。交给错误处理器处理。
        if (that.specialErrorHandler(response)) {
          console.log('specialErrorHandler 返回 true，调用 errorCallback');
          SafeUtil.safeCallback(errorCallback)(response);
          return;
        }

        console.log('specialErrorHandler 返回 false，调用 successCallback');
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (err: any) {
        let response = err.response;
        console.log('=== httpPost 错误回调 ===');
        console.log('错误响应:', response);

        //特殊错误情况的通用处理方式
        if (that.specialErrorHandler(response)) {
          console.log('specialErrorHandler 返回 true，跳过默认错误处理');
          return;
        }

        console.log('specialErrorHandler 返回 false，调用默认错误处理');
        that.defaultErrorHandler(response, errorCallback);
      },
      function (res: any) {
        that.loading = false;
        that.updateUI();
        SafeUtil.safeCallback(finallyCallback)(res);
      },
      opts
    );
  }
}